/*
  Contact page (sections/noma-contact.liquid). Three small enhancements; the form works without any of them.
  1. VIN is upper-cased while typing and stripped of spaces when the field loses focus.
  2. When Shopify sends the page back with an error (for example an e-mail address it rejects), the values typed in the
     custom fields (Requested part, Year, Make, Model, VIN) are not repopulated by Liquid. They are kept in
     sessionStorage for that one round trip and put back here; the copy is removed as soon as it has been put back.
  3. The send button ignores a second click while the first request is on its way.
  4. After a send the thank-you panel or the error summary gets keyboard focus (screen readers read it) and is scrolled
     into view below the sticky header. This runs after the load event: the browser's own jump to the #ContactForm
     anchor (Shopify's form action) happens after deferred scripts and would otherwise take the focus away again.
  5. Vehicle rule (owner, 2026-10-07): the request is only sent with year + make + model, or a VIN of exactly 17 characters
     (letters and numbers, never I, O or Q). A VIN that is typed in must be right even when year, make and model are there.
     WHY a window capture listener + stopImmediatePropagation (same as assets/noma-offer.js): Shopify's spam protection
     (script#captcha-bootstrap) listens to 'submit' on the document, ignores preventDefault and sends the form itself.
  6. Spam check: a request that passes gets the check word in the hidden "Form check" field, unless the invisible trap
     field ("Website", filled only by robots) has text. The mailbox filter files Shopify contact e-mails without the
     word into a label instead of the inbox; robots that post to /contact directly never run this script.
*/
(function () {
  'use strict';

  var FORM_ID = 'ContactForm';
  var STORE_KEY = 'noma-contact-draft';
  var CHECK_WORD = 'nmformok'; /* the mailbox filter looks for this word: change both together */
  var VIN_CHARS = /^[A-HJ-NPR-Z0-9]{17}$/;
  var YEAR = /^(19|20)\d{2}$/;

  function storage() {
    try {
      return window.sessionStorage;
    } catch (e) {
      return null;
    }
  }

  /* the fields a person types in (not the spam trap or the hidden check field) */
  function fieldsOf(form) {
    var list = form.querySelectorAll('input[name^="contact["]:not([type="hidden"]):not([data-noma-hp]), textarea[name^="contact["]');
    return Array.prototype.slice.call(list);
  }

  function vehicleFields(form) {
    return {
      year: form.querySelector('#ContactForm-year'),
      make: form.querySelector('#ContactForm-make'),
      model: form.querySelector('#ContactForm-model'),
      vin: form.querySelector('#ContactForm-vin')
    };
  }

  /* null when the vehicle part is fine, else { text, fields: [the fields to mark] } */
  function vehicleProblem(form) {
    var f = vehicleFields(form);
    if (!f.year || !f.make || !f.model || !f.vin) return null;
    var vin = f.vin.value.replace(/\s+/g, '').toUpperCase();
    if (vin) {
      if (vin.length !== 17) {
        return { fields: [f.vin], text: 'A VIN has exactly 17 characters (you entered ' + vin.length + '). Check it, or clear it and fill in year, make and model.' };
      }
      if (!VIN_CHARS.test(vin)) {
        return { fields: [f.vin], text: 'A VIN has only letters and numbers and never the letters I, O or Q. Check it, or clear it and fill in year, make and model.' };
      }
      return null;
    }
    var empty = [f.year, f.make, f.model].filter(function (field) { return !field.value.trim(); });
    if (empty.length) return { fields: empty, text: 'Fill in year, make and model, or the 17-character VIN.' };
    if (!YEAR.test(f.year.value.trim())) return { fields: [f.year], text: 'Enter the year as 4 digits, for example 2014.' };
    return null;
  }

  function showVehicleProblem(form, problem) {
    var f = vehicleFields(form);
    [f.year, f.make, f.model, f.vin].forEach(function (field) {
      if (field) field.setAttribute('aria-invalid', problem && problem.fields.indexOf(field) !== -1 ? 'true' : 'false');
    });
    var box = form.querySelector('[data-noma-vehicle-error]');
    if (box) {
      box.textContent = problem ? problem.text : '';
      box.hidden = !problem;
    }
    if (problem) problem.fields[0].focus();
  }

  /* runs before Shopify's own submit handling (see 5. above); registered once for every version of the form */
  window.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || form.id !== FORM_ID) return;
    var problem = vehicleProblem(form);
    if (problem) {
      event.preventDefault();
      event.stopImmediatePropagation();
      showVehicleProblem(form, problem);
      return;
    }
    var trap = form.querySelector('[data-noma-hp]');
    var check = form.querySelector('[data-noma-check]');
    if (check) check.value = trap && trap.value ? '' : CHECK_WORD;
  }, true);

  function saveDraft(form) {
    var store = storage();
    if (!store) return;
    var draft = {};
    fieldsOf(form).forEach(function (field) {
      if (field.value) draft[field.name] = field.value;
    });
    try {
      store.setItem(STORE_KEY, JSON.stringify(draft));
    } catch (e) {
      /* storage full or blocked: the form just starts empty */
    }
  }

  function clearDraft() {
    var store = storage();
    if (!store) return;
    try {
      store.removeItem(STORE_KEY);
    } catch (e) {
      /* nothing to do */
    }
  }

  function restoreDraft(form) {
    var store = storage();
    var draft = null;
    if (!store) return;
    try {
      draft = JSON.parse(store.getItem(STORE_KEY) || 'null');
    } catch (e) {
      draft = null;
    }
    if (!draft || typeof draft !== 'object') return;
    fieldsOf(form).forEach(function (field) {
      if (!field.value && typeof draft[field.name] === 'string') field.value = draft[field.name];
    });
  }

  function init() {
    var form = document.getElementById(FORM_ID);
    if (!form || form.getAttribute('data-noma-ready')) return;
    form.setAttribute('data-noma-ready', 'true');

    var root = form.closest('[data-noma-contact]') || document;
    /* a draft is only used for the page view that follows a failed send; every other view (fresh, or the thank-you) drops it */
    if (root.querySelector('[data-noma-contact-error]')) restoreDraft(form);
    clearDraft();

    var vin = form.querySelector('#ContactForm-vin');
    if (vin) {
      vin.addEventListener('input', function () {
        var start = vin.selectionStart;
        var end = vin.selectionEnd;
        var upper = vin.value.toUpperCase();
        if (upper === vin.value) return;
        vin.value = upper;
        try {
          vin.setSelectionRange(start, end);
        } catch (e) {
          /* some input types have no selection */
        }
      });
      vin.addEventListener('blur', function () {
        vin.value = vin.value.replace(/\s+/g, '').toUpperCase();
      });
    }

    /* the vehicle message goes away as soon as one of the four fields is changed */
    var vf = vehicleFields(form);
    [vf.year, vf.make, vf.model, vf.vin].forEach(function (field) {
      if (field) field.addEventListener('input', function () { showVehicleProblem(form, null); });
    });

    var button = form.querySelector('button[type="submit"]');
    form.addEventListener('submit', function (event) {
      if (form.getAttribute('data-noma-sending')) {
        event.preventDefault();
        return;
      }
      if (vin) vin.value = vin.value.replace(/\s+/g, '').toUpperCase();
      form.setAttribute('data-noma-sending', 'true');
      if (button) button.setAttribute('aria-busy', 'true');
      saveDraft(form);
    });

    /* back / forward cache: the page comes back as it was left, so release the button */
    window.addEventListener('pageshow', function (event) {
      if (!event.persisted) return;
      form.removeAttribute('data-noma-sending');
      if (button) button.removeAttribute('aria-busy');
    });

    var status = root.querySelector('[data-noma-contact-error], [data-noma-contact-success]');
    if (status) {
      if (document.readyState === 'complete') reveal(status);
      else window.addEventListener('load', function () { reveal(status); });
    }
  }

  function reveal(status) {
    /* next tick: the anchor jump of the browser has run by then */
    window.setTimeout(function () {
      if (!status.isConnected) return;
      try {
        status.focus({ preventScroll: true });
      } catch (e) {
        status.focus();
      }
      var header = document.querySelector('.section-header');
      var top = header ? header.getBoundingClientRect().bottom : 0;
      var rect = status.getBoundingClientRect();
      if (rect.top < top + 8 || rect.top > window.innerHeight - 80) status.scrollIntoView({ block: 'center' });
    }, 0);
  }

  init();
  /* theme editor: the section is re-rendered in place */
  document.addEventListener('shopify:section:load', init);
})();
